import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Paper, Grow, Fade } from '@mui/material';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { FormProvider, useForm } from 'react-hook-form';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import {
  endedCallDataAtom,
  selectedBranchAtom,
  selectedLeadIdAtom,
  additionalCustomerDataAtom,
  reloadLeadsAtom,
} from '../../../../Atoms';
import CustomerSectionPage from '../../../CustomerPage/LazyCustomerSectionPage';
import ChatBox from '../../../CustomerPage/ChatSection/ChatBox';
import useAPI from '../../../../api';
import useInfiniteQuery from '../../../../Hooks/useInfiniteQuery';
import {
  notifySMSReceivedAtom,
  notifySMSSentAtom,
} from '../../../../Atoms/HubAtoms';
import EmailWindow from '../../../CustomerPage/EmailSection/EmailWindow';
import TemplateMessageDialog from '../../../../Components/Dialog/TemplateMessageDialog';
import SmsAfterCallWrapper from '../../../CustomerPage/Shared/SmsAfterCallWrapper';
import groupTemplatesByName from '../../../../Helpers/Arrays/groupTemplatesByName';
import '../../../../Styles/app.scss';
import LeadDialog, {
  DEFAULT_PAGES,
} from '../../../../Components/Dialog/EntityInfoDialog';
import ConfirmPopup from '../../../../Components/Popup/ConfirmPopup';
import LeadDetailsPage from './LeadDetailsPage';
import useLocationName from '../../../../Hooks/useLocationName';
import useSendEmail from '../../../CustomerPage/Utils/useSendEmail';
import useNotify from '../../../../Hooks/useNotify';
import withItalianPrefix from '../../../../Helpers/StringsFormatting/withItalianPrefix';

const LeadComponents = function LeadComponents() {
  const API = useAPI();
  const methods = useForm();
  const intl = useIntl();
  const locationName = useLocationName();
  const leadId = useAtomValue(selectedLeadIdAtom);
  const [additionalCustomerData, setAdditionalCustomerData] = useAtom(
    additionalCustomerDataAtom
  );
  const notifySMSSent = useAtomValue(notifySMSSentAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const notifySMSReceived = useAtomValue(notifySMSReceivedAtom);
  const { notify, notifyError } = useNotify();
  const setReloadLeads = useSetAtom(reloadLeadsAtom);
  const [endedCallData, setEndedCallData] = useAtom(endedCallDataAtom);

  const [chatOpen, setChatOpen] = useState(null);
  const [endCallMessage, setEndCallMessage] = useState(null);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [selectedEmailAddress, setSelectedEmailAddress] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactIndex, setContactIndex] = useState(0);

  const [emailWindowOpen, setEmailWindowOpen] = useState(null);
  const [sendEndCallMessage, setSendEndCallMessage] = useState(false);
  const [leadDialog, setLeadDialog] = useState(null);
  const [customerUnreadMessages, setCustomerUnreadMessages] = useState(0);
  const [smsSubmit, setSmsSubmit] = useState(false);
  const [confirmClosing, setConfirmClosing] = useState(null);
  const [leadInformationData, setLeadInformationData] = useState(null);

  const handleOpenEmailWindow = () => {
    setEmailWindowOpen('SEND');
  };
  const handleReplyMail = (data) => {
    setEmailWindowOpen(data);
  };
  const handleCloseEmailWindow = () => {
    setEmailWindowOpen(null);
  };
  const openDialog = (leadData) => {
    setLeadDialog(leadData);
  };

  const handleChangeContactIndex = (index) => {
    setContactIndex(index);
  };

  const getLead = useCallback(() => {
    if (leadId && selectedBranch) {
      API.getLead(
        leadId,
        selectedBranch.id,
        (result) => {
          setSelectedContact(result);
          setLeadInformationData({
            ...result,
            ...(result?.contacts[0] ?? {}),
            mobilePhoneNumber:
              result?.contacts[0]?.contactPhones?.find(
                ({ phoneTypeId }) => phoneTypeId === 1
              )?.phoneNumber || '',
            landlinePhoneNumber:
              result?.contacts[0]?.contactPhones?.find(
                ({ phoneTypeId }) => phoneTypeId === 3
              )?.phoneNumber || '',
            email: result?.contacts[0]?.contactEmailAddresses?.find(
              ({ emailTypeId }) => emailTypeId === 1
            )?.email,
            ...result?.contacts[0]?.contactAddresses?.find(
              ({ addressTypeId }) => addressTypeId === 1
            ),
          });
        },
        (err) => {
          console.log(err);
        }
      );
    }
  }, [leadId, selectedBranch]);

  useEffect(() => {
    getLead();
  }, [leadId, selectedBranch]);

  useEffect(() => {
    if (selectedContact) {
      setSelectedPhoneNumber(
        selectedContact.contacts[0].contactPhones?.find(
          ({ priority, phoneNumber }) => priority && phoneNumber
        )?.phoneNumber || null
      );
      setSelectedEmailAddress(
        selectedContact?.contacts[0]?.contactEmailAddresses?.find(
          ({ active }) => active
        )?.email || null
      );
    }
  }, [selectedContact]);

  const getMessages = useCallback(
    (pageNumber, signal) => {
      return leadId
        ? API.getEntityMessages(leadId, false, {
            pageNumber,
            silent: true,
            signal,
          })
        : Promise.resolve([]);
    },
    [leadId]
  );

  const {
    items: chatMessages,
    recordCount,
    setNewItems,
    ObserverWrapper,
  } = useInfiniteQuery(getMessages);

  useEffect(() => {
    if (selectedBranch && leadId) {
      API.getUnreadMessagesCount(
        leadId,
        (result) => {
          setCustomerUnreadMessages(result);
        },
        (err) => {
          console.log(err);
        }
      );
    }
  }, [leadId]);

  const handleSendEmail = useSendEmail({
    selectedEmailAddress,
    endCallMessage,
    setEndCallMessage,
    setSendEndCallMessage,
    onClose: handleCloseEmailWindow,
  });

  useEffect(() => {
    if (endCallMessage) {
      if (
        (endCallMessage?.channel === 'BOTH' ||
          endCallMessage?.channel === 'SMS') &&
        endCallMessage?.message
      )
        API.sendMessage(
          {
            templateId: endCallMessage?.templateId,
            activityId: endCallMessage?.activityId,
            branchId: endCallMessage?.branchId,
            recipients: endCallMessage?.recipients,
            message: endCallMessage?.message,
          },
          (result) => {
            notify('MESSAGE_SUCCESSFULLY_SENT', 'success');
            setNewItems(result[0], recordCount + 1);
            setEndCallMessage(null);
            setSendEndCallMessage(false);
            setEndedCallData(null);
          },
          () => {
            notify('SMS_ERROR', 'error');
            setSendEndCallMessage(false);
          }
        );
    }
  }, [endCallMessage]);

  const handleSendMessage = (newMessage) => {
    API.sendMessage(
      {
        branchId: selectedBranch.id,
        recipients: [leadId], //array of guids
        message: newMessage,
      },
      (result) => {
        setNewItems(result[0], recordCount + 1);
      },
      () => {
        notify('SMS_ERROR', 'error');
      }
    );
  };

  useEffect(() => {
    //increment when 'notifySMSReceived': message is received from the client with provided id
    if (notifySMSReceived?.baseEntityId === leadId) {
      setCustomerUnreadMessages(customerUnreadMessages + 1);
      setNewItems(notifySMSReceived, recordCount + 1);
    }
  }, [notifySMSReceived?.id]);

  useEffect(() => {
    //increment when 'notifySMSSent': message is sended from the user
    if (
      notifySMSSent?.baseEntityId === leadId &&
      !chatMessages?.find((x) => x.id === notifySMSSent?.id)
    ) {
      setNewItems(notifySMSSent, recordCount + 1);
    }
  }, [notifySMSSent?.id]);

  const handleOpenChat = (event) => {
    setChatOpen(event.currentTarget);
  };

  const handleCloseChat = () => {
    setChatOpen(null);
  };

  const DialActions = [
    {
      icon: <SmsOutlinedIcon />,
      name: 'SMS',
      onClick: handleOpenChat,
      badgeContent: customerUnreadMessages,
      disabled: false,
    },
    {
      icon: <EmailOutlinedIcon />,
      name: 'Email',
      onClick: handleOpenEmailWindow,
      disabled: false,
    },
  ];

  useEffect(() => {
    if (!leadId) {
      setSelectedContact(null); //unmounts customer details
    }
  }, [leadId]);

  const checkSubmit = (value) => {
    setSmsSubmit(value);
  };

  const handleSubmit = (data) => {
    const { email, ...rest } = data;

    API.updateLeads(
      {
        ...rest,
        ...(data?.mobilePhoneNumber
          ? {
              mobilePhoneNumber: withItalianPrefix(data.mobilePhoneNumber),
            }
          : {}),
        ...(email ? { email } : {}),
      },
      () => {
        setLeadDialog(null);
        setAdditionalCustomerData({
          customerName: `${data.contacts?.[0].firstName ?? ''} ${data.contacts?.[0].middleName ?? ''} ${data.contacts?.[0].lastName ?? ''}`,
        });
        getLead();
        setReloadLeads(true);
        notify('LEAD_EDITED_SUCCESSFULLY', 'success');
      },
      (err) => {
        notifyError('EDIT_LEAD_ERROR', err);
      }
    );
  };

  const open = Boolean(chatOpen);
  return (
    <>
      <Fade in={Boolean(leadId)} unmountOnExit>
        <div style={{ width: '100%', height: '100%' }}>
          {selectedContact && (
            <>
              <CustomerSectionPage
                isLead
                locationName={locationName}
                customerId={leadId}
                openReply={handleReplyMail}
                activityParentId={
                  additionalCustomerData?.recallTime
                    ? additionalCustomerData.parentId
                    : null
                }
                selectedPhoneNumber={selectedPhoneNumber}
                setSelectedPhoneNumber={setSelectedPhoneNumber}
                selectedEmailAddress={selectedEmailAddress}
                setSelectedEmailAddress={setSelectedEmailAddress}
                selectedCustomer={selectedContact}
                contactIndex={contactIndex}
                handleChangeContactIndex={handleChangeContactIndex}
                openDialog={openDialog}
                actions={DialActions}
                customerUnreadMessages={customerUnreadMessages}
              />

              <FormProvider {...methods}>
                <TemplateMessageDialog
                  id="sms-aftercall"
                  title={intl.messages.NEW_MESSAGE}
                  submitDisabled={!smsSubmit}
                  handleSubmit={() => setSendEndCallMessage(true)}
                  open={
                    endedCallData !== null &&
                    endedCallData.outcome !== 9 && //wrong number
                    endedCallData.outcome !== 18 && //inexistent number
                    endedCallData.messageTemplates.length > 0 &&
                    selectedPhoneNumber.substring(0, 4) === '+393'
                  } //only if mobile phone number
                  handleClose={() => {
                    setEndedCallData(null);
                  }}
                  widgetWrapper={
                    <SmsAfterCallWrapper
                      failedChecks={endedCallData?.failedChecks}
                      checkSubmit={checkSubmit}
                      customerId={selectedContact?.id || leadId}
                      activityId={endedCallData?.id}
                      setEndCallMessage={setEndCallMessage}
                      selectedBranch={selectedBranch}
                      sendEndCallMessage={sendEndCallMessage}
                      templates={groupTemplatesByName(
                        endedCallData?.messageTemplates
                      )}
                      isEmailAddressAvailable={
                        selectedEmailAddress !== null && locationName !== 'SPOT'
                      }
                      customMessage={locationName === 'SPOT'}
                    />
                  }
                />
              </FormProvider>
              <Grow in={open}>
                <Paper
                  id="chatbox"
                  elevation={6}
                  sx={{
                    position: 'absolute',
                    overflow: 'hidden',
                    display: 'flex',
                    bottom: '16px',
                    right: '16px',
                    height: '80%',
                    zIndex: 1050,
                  }}
                >
                  <ChatBox
                    subtractSmsNumber={() => {
                      setCustomerUnreadMessages(customerUnreadMessages - 1);
                    }}
                    close={handleCloseChat}
                    ObserverWrapper={ObserverWrapper}
                    handleSendMessage={handleSendMessage}
                    messages={chatMessages}
                  />
                </Paper>
              </Grow>
              <EmailWindow
                open={Boolean(emailWindowOpen)}
                replyData={emailWindowOpen === 'SEND' ? null : emailWindowOpen}
                close={handleCloseEmailWindow}
                onSubmit={handleSendEmail}
                defaultEmail={selectedEmailAddress}
              />
            </>
          )}
        </div>
      </Fade>
      <LeadDialog
        title={intl.messages.EDIT_LEAD}
        onSubmit={handleSubmit}
        open={Boolean(leadDialog)}
        data={leadInformationData}
        pages={[LeadDetailsPage, ...DEFAULT_PAGES]}
        edit
        close={(e, isFormDirty) => {
          //isFormDirty is a boolean that checks if the user has changed at least one fields values
          if (isFormDirty) setConfirmClosing(e.currentTarget);
          else {
            setLeadDialog(null); //if no fields touched the dialog close immediatly without confirmation
          }
        }}
        branchId={selectedBranch.id}
      />
      <ConfirmPopup
        confirmText={intl.messages.CLOSE_WINDOW}
        open={Boolean(confirmClosing)}
        anchorEl={confirmClosing}
        close={() => setConfirmClosing(null)}
        handleConfirm={() => setLeadDialog(null)}
      />
    </>
  );
};

export default LeadComponents;
